/**
 * Key plumbing for the vault form's companion sections.
 *
 * The shared entry form is one flat map of `key -> value`. A companion's
 * fields are the same catalog fields as the account holder's (a passport
 * number is a passport number), so they are told apart by prefixing the key
 * with the companion's vault subject — `companion:abc/passport_number` — and
 * split back out on submit into the account holder's `values` and a
 * per-subject `companion_values`, which is the shape the consume endpoint
 * persists under.
 */

const SEPARATOR = "/";

export function companionFieldKey(subject: string, key: string): string {
  return `${subject}${SEPARATOR}${key}`;
}

/**
 * The form key for a value the server names by subject and catalog key —
 * the reverse of `splitVaultValues`. The account holder's subject is `""`.
 */
export function formFieldKey(subject: string, key: string): string {
  return subject === "" ? key : companionFieldKey(subject, key);
}

/** The catalog field key, with any companion prefix removed. */
export function baseFieldKey(key: string): string {
  const at = key.indexOf(SEPARATOR);
  return at < 0 ? key : key.slice(at + 1);
}

/** The companion subject a key is prefixed with, or null for the account holder's. */
export function subjectOfKey(key: string): string | null {
  const at = key.indexOf(SEPARATOR);
  return at < 0 ? null : key.slice(0, at);
}

/** Re-key catalog-keyed values (e.g. a passport scan) for one section. */
export function prefixValues(
  values: Record<string, string>,
  subject: string | null,
): Record<string, string> {
  if (subject === null) return values;
  return Object.fromEntries(
    Object.entries(values).map(([key, value]) => [
      companionFieldKey(subject, key),
      value,
    ]),
  );
}

export interface SplitVaultValues {
  values: Record<string, string>;
  companion_values: Record<string, Record<string, string>>;
}

/** Split the flat form map into the submit body's two halves. */
export function splitVaultValues(
  flat: Record<string, string>,
): SplitVaultValues {
  const values: Record<string, string> = {};
  const companion_values: Record<string, Record<string, string>> = {};
  for (const [key, value] of Object.entries(flat)) {
    const subject = subjectOfKey(key);
    if (subject === null) {
      values[key] = value;
    } else {
      (companion_values[subject] ??= {})[baseFieldKey(key)] = value;
    }
  }
  return { values, companion_values };
}
